import { Badge, Button } from 'antd'
import React, { useState } from 'react'
import Card from 'components/Card'
import colors from 'global/styles/colors'
import ContainerNotes from '../ContainerNotes'
import CutContainer from '../CutContainer/CutContainer'
import featureCodes from 'constants/featureCodes'
import { InfoOutlined } from '@ant-design/icons'
import LogbookRemarks from '../LogbookRemarks'
import MoreInfoContent from './MoreInfoContent'
import MoreInfoModal from 'components/MoreInfoModal'
import NavigationTab from 'components/NavigationTabs'
import PropTypes from 'prop-types'
import propTypes from 'global/propTypes'
import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'
import { useTranslation } from 'react-i18next'

import './logbookAndContainerNotes.less'

const TAB_KEYS = {
  CONTAINER_NOTES: 'container-note',
  CUT_CONTAINER: 'cut-container',
  LOGBOOK: 'logbook',
}

const LogbookAndContainerNotes = ({
  isTimestampUTC,
  isLoadingLogbook,
  isLoadingContainerNotes,
  logbookRemarks,
  containerNotes,
  numberOfLogbookRemarks,
  numberOfContainerNotes,
}) => {
  const { t } = useTranslation()
  const showContainerNotes = useFeatureAccessControlEnabled(
    featureCodes.SHOW_CONTAINER_NOTES
  )

  const [activeTab, setActiveTab] = useState(TAB_KEYS.LOGBOOK)
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false)

  // When tabs are shown, the more-info icon moves inline with the tab bar,
  // so the Card itself shouldn't render its own more-info button/header icon.
  const cardMoreInfoProps = showContainerNotes
    ? {}
    : {
        moreInfoContent: (
          <MoreInfoContent showContainerNotes={showContainerNotes} />
        ),
        moreInfoTitle: t('portCall.logbookAndContainerNotes.title'),
      }

  // Logbook Tab Content - Using LogbookRemarks component
  const logbookTabContent = (
    <div className="logbook-notes__logbook-wrapper">
      <LogbookRemarks
        isLoading={isLoadingLogbook}
        isTimestampUTC={isTimestampUTC}
        noOfRemarks={numberOfLogbookRemarks}
        remarks={logbookRemarks}
      />
    </div>
  )

  // Container Notes Tab Content - Using ContainerNotes component
  const containerNotesTabContent = (
    <div className="logbook-notes__container-notes-wrapper">
      <ContainerNotes
        isLoading={isLoadingContainerNotes}
        isTimestampUTC={isTimestampUTC}
        noOfNotes={numberOfContainerNotes}
        notes={containerNotes}
      />
    </div>
  )

  // NavigationTab items configuration
  const navigationTabItems = [
    {
      children: logbookTabContent,
      key: TAB_KEYS.LOGBOOK,
      label: (
        <span>
          {t('portCall.logbook.logbook')}
          <Badge
            count={numberOfLogbookRemarks}
            style={{
              backgroundColor: colors.brandOrange,
              marginLeft: '0.5rem',
            }}
          />
        </span>
      ),
    },
    ...(showContainerNotes
      ? [
          {
            children: containerNotesTabContent,
            key: TAB_KEYS.CONTAINER_NOTES,
            label: (
              <span>
                {t('portCall.containerNotebook.containerNotebook')}
                <Badge
                  count={numberOfContainerNotes}
                  style={{
                    backgroundColor: colors.brandOrange,
                    marginLeft: '0.5rem',
                  }}
                />
              </span>
            ),
          },
        ]
      : []),
    {
      children: ( <div className="logbook-notes__cut-container-wrapper"><CutContainer /></div>),
      key: TAB_KEYS.CUT_CONTAINER,
      label: <span>{t('portCall.cutContainer')}</span>,
    },
  ]

  return (
    <Card className="logbook-notes__container" {...cardMoreInfoProps}>
      {showContainerNotes && (
        <>
          <NavigationTab
            activeKey={activeTab}
            className="logbook-notes__tabs"
            items={navigationTabItems}
            onTabChange={setActiveTab}
            tabBarExtraContent={
              <Button
                className="logbook-notes__tabs-more-info"
                icon={<InfoOutlined />}
                onClick={() => setShowMoreInfoModal(true)}
                shape="circle"
                size="small"
              />
            }
          />
          <MoreInfoModal
            isVisible={showMoreInfoModal}
            onClose={() => setShowMoreInfoModal(false)}
            title={t('portCall.logbookAndContainerNotes.title')}
          >
            <MoreInfoContent showContainerNotes={showContainerNotes} />
          </MoreInfoModal>
        </>
      )}

      {!showContainerNotes && (
        <LogbookRemarks
          isLoading={isLoadingLogbook}
          isTimestampUTC={isTimestampUTC}
          noOfRemarks={numberOfLogbookRemarks}
          remarks={logbookRemarks}
        />
      )}
    </Card>
  )
}

LogbookAndContainerNotes.defaultProps = {
  containerNotes: [],
  isTimestampUTC: false,
  logbookRemarks: [],
}

LogbookAndContainerNotes.propTypes = {
  containerNotes: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      cellNumber: PropTypes.string,
      completedAt: PropTypes.string,
      containerNo: PropTypes.string,
      image: PropTypes.shape({
        fullUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
      isComplete: PropTypes.bool,
      loggedAt: PropTypes.string.isRequired,
      remark: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
    })
  ),
  isLoadingContainerNotes: PropTypes.bool.isRequired,
  isLoadingLogbook: PropTypes.bool.isRequired,
  isTimestampUTC: PropTypes.bool,
  logbookRemarks: propTypes.logbookRemarks,
  numberOfContainerNotes: PropTypes.number.isRequired,
  numberOfLogbookRemarks: PropTypes.number.isRequired,
}

export default LogbookAndContainerNotes
